/**
 * Сборка query params для реестра цен: пустой фильтр = omit (все значения),
 * false/0 сохраняем, scope оффера/партнёра не теряем.
 */

export type PrivateRatesScope = {
    offerId?: number | null;
    affiliateId?: number | null;
};

export type PrivateRatesRouteContext = {
    routeName?: string | null;
    routeParamId?: string | number | null;
    offerLegacyId?: number | null;
    affiliateId?: number | null;
};

export type DefaultFilterShape = {
    id: string;
    type?: string;
    defaultValue?: unknown;
};

export function resolvePrivateRatesScope(ctx: PrivateRatesRouteContext): PrivateRatesScope {
    const routeId = Number(ctx.routeParamId);
    const finiteRouteId = Number.isFinite(routeId) ? routeId : null;

    if (ctx.routeName === 'offer') {
        const offerId = ctx.offerLegacyId ?? finiteRouteId;
        return offerId != null ? { offerId: Number(offerId) } : {};
    }

    if (ctx.routeName === 'affiliate') {
        const affiliateId = ctx.affiliateId ?? finiteRouteId;
        return affiliateId != null ? { affiliateId: Number(affiliateId) } : {};
    }

    return {};
}

export function hasFilterDefaultValue(defaultValue: unknown): boolean {
    if (Array.isArray(defaultValue)) {
        return defaultValue.length > 0;
    }
    return !!defaultValue;
}

export function shouldApplyDefaultFilter(
    filterId: string,
    queryParams: Record<string, unknown>,
    routeName: string | null | undefined,
): boolean {
    const isEmptyParams = Object.keys(queryParams).length === 0;
    const isOfferOrAffiliate = routeName === 'offer' || routeName === 'affiliate';
    const isReset = isEmptyParams || isOfferOrAffiliate;

    return isReset || filterId in queryParams;
}

export function getDefaultFilterSelectValue(filter: DefaultFilterShape): unknown {
    if (filter.type === 'boolean') {
        return filter.defaultValue === 'true' || filter.defaultValue === true;
    }
    return filter.defaultValue;
}

export function buildDefaultFilterParam(filter: DefaultFilterShape): Record<string, unknown> | null {
    if (!hasFilterDefaultValue(filter.defaultValue)) {
        return null;
    }

    if (filter.type === 'boolean') {
        const value = getDefaultFilterSelectValue(filter);
        return { [filter.id]: value };
    }

    if (filter.id === 'offer_id' || filter.id === 'affiliate_id') {
        const items = filter.defaultValue as Array<{ legacy_id?: number; id?: number }>;
        return { [filter.id]: items.map((i) => i.legacy_id ?? i.id) };
    }

    return { [filter.id]: filter.defaultValue };
}

export function buildDefaultFiltersParamsObject(
    filters: DefaultFilterShape[],
    queryParams: Record<string, unknown>,
    routeName: string | null | undefined,
): Record<string, unknown> {
    const paramsObj: Record<string, unknown> = {};

    filters.forEach((filter) => {
        if (!shouldApplyDefaultFilter(filter.id, queryParams, routeName)) {
            return;
        }
        const entry = buildDefaultFilterParam(filter);
        if (entry) {
            Object.assign(paramsObj, entry);
        }
    });

    return paramsObj;
}

export function omitEmptyFilterParams(
    params: Record<string, unknown>,
): Record<string, unknown> {
    const result: Record<string, unknown> = { ...params };

    Object.keys(result).forEach((key) => {
        const value = result[key];
        if (value === '' || value === null || value === undefined) {
            delete result[key];
            return;
        }
        if (Array.isArray(value) && value.length === 0) {
            delete result[key];
        }
    });

    return result;
}

export function normalizeRateStatus(value: unknown): string[] | undefined {
    if (value === '' || value === null || value === undefined) {
        return undefined;
    }
    if (!Array.isArray(value) || value.length === 0) {
        return undefined;
    }

    const ids = value
        .map((item) => {
            if (item !== null && typeof item === 'object' && 'id' in item) {
                return String((item as { id: string | number }).id);
            }
            return item === null || item === undefined ? '' : String(item);
        })
        .filter((id) => id.length > 0);

    return ids.length > 0 ? ids : undefined;
}

export function withPrivateRatesScopeParams(
    params: Record<string, unknown>,
    scope: PrivateRatesScope,
): Record<string, unknown> {
    const result = omitEmptyFilterParams({ ...params });

    if (scope.offerId != null) {
        result.offer_id = [scope.offerId];
    }
    if (scope.affiliateId != null) {
        result.affiliate_id = [scope.affiliateId];
    }

    // OfferPage иногда дописывает ключ, которого нет в API registry
    delete result.offer_legacy_id;

    if ('rate_status' in result) {
        const normalized = normalizeRateStatus(result.rate_status);
        if (normalized) {
            result.rate_status = normalized;
        } else {
            delete result.rate_status;
        }
    }

    return omitEmptyFilterParams(result);
}
